import { Search } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { queryStringToObject } from "@outschool/routes";
import { dayjs } from "@outschool/time";

import { AvailableTime } from "../AvailableTimesInput/types";
import { TimeOfDayOption, checkIsSelected } from "../AvailableTimesInput/utils";
import {
  ClassSizeType,
  LearnerAttributesType,
  LearningFormatSearchTerms,
  SOURCE_MAP,
  TIME_OF_DAY_OPTIONS,
} from "./consts";

export function getSourceFromQueryParams() {
  const queries = queryStringToObject(window.location.search);
  const foundEntry = Object.entries(SOURCE_MAP).find(([key]) =>
    Boolean(queries[key]),
  );

  if (!foundEntry) {
    return null;
  }

  const [key, mappedValue] = foundEntry;

  // For variant, return the actual query parameter value instead of the mapped value
  if (key === "variant") {
    return queries[key] || null;
  }

  return mappedValue;
}

export const findSelectedTimeOptionFromAvailableTimes = (
  options: TimeOfDayOption[],
  av: AvailableTime[],
) => {
  return options
    .filter(tod => tod.range[0] && tod.range[1])
    .find(tod => checkIsSelected(av, tod.range[0]!, tod.range[1]!, tod.days));
};

export const onChangeMultiSelect = (
  selectedValues: string[],
  changedValue: string,
  onChange: (values: string[]) => void,
) => {
  const newSelectedValues = selectedValues.includes(changedValue)
    ? selectedValues.filter(selectedValue => selectedValue !== changedValue)
    : [...selectedValues, changedValue];
  onChange(newSelectedValues);
  return newSelectedValues;
};

export const getAgeFromBirthMonthAndYear = (month: number, year: number) => {
  const firstDayOfBirthMonth = dayjs().year(year).month(month).startOf("month");
  return Number(dayjs().diff(firstDayOfBirthMonth, "year"));
};

export const deriveSearchFiltersFromAttributes = (
  learnerAttributes: LearnerAttributesType,
): SearchFiltersType => {
  const {
    birthMonth,
    birthYear,
    subjects,
    learningFormats,
    classSizes,
    availableTimes,
  } = learnerAttributes;

  const age =
    !!birthMonth && !!birthYear
      ? getAgeFromBirthMonthAndYear(birthMonth, birthYear).toString()
      : null;
  const theme = subjects?.join(", ") || null;
  const format =
    learningFormats?.map(value => LearningFormatSearchTerms[value]).join(",") ||
    null;

  let capacityMax = null;
  let capacityMin = null;
  // if none or both class size options are selected, ignore size filters
  if (classSizes?.length === 1) {
    const classSize = classSizes[0];
    if (classSize === ClassSizeType.OneOnOne) {
      capacityMax = 1;
    } else if (classSize === ClassSizeType.Group) {
      capacityMin = 2;
    }
  }

  let startAfterTime = null;
  let endByTime = null;
  let dow = null;
  if (!!availableTimes?.length) {
    const matchedOption = findSelectedTimeOptionFromAvailableTimes(
      TIME_OF_DAY_OPTIONS,
      availableTimes,
    );
    // only populate time of day for preset availabilty options, not custom schedules
    if (matchedOption) {
      startAfterTime = matchedOption.range?.[0] || null;
      endByTime = matchedOption.range?.[1] || null;
    }

    const daysOfWeekWithAvailability = [
      ...new Set(
        availableTimes.map(
          (availability: AvailableTime) =>
            Search.DAYS_OF_WEEK[availability.day],
        ),
      ),
    ];
    // include any days of the week with availability
    if (daysOfWeekWithAvailability.length) {
      dow = daysOfWeekWithAvailability.join(",");
    }
  }

  return {
    age,
    theme,
    format,
    capacityMax,
    capacityMin,
    startAfterTime,
    endByTime,
    dow,
  };
};
